import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import UserMenu from './UserMenu';

/**
 * The header every signed-in screen shares.
 *
 * It exists because the theme toggle and the account menu have to be reachable from
 * everywhere, not just the list. Four screens were each rolling their own header, and three
 * of them had no way to reach your account at all — you had to go back to the list first.
 *
 * Two shapes. The list leads with the app mark and puts the search field in the bar; every
 * other screen leads with a back link and its own name. Both end in the same actions, so the
 * right-hand side of the bar never moves between screens.
 */
export default function AppHeader({
  title,
  back = { to: '/', label: 'Notes' },
  children,
}: {
  /** Omitted on the list, which shows the app mark instead. */
  title?: string;
  /** Where the back link goes. A collaborative note returns to its own list, not to `/`. */
  back?: { to: string; label: string };
  /** The search field, on the list only. */
  children?: ReactNode;
}) {
  return (
    <header>
      {title ? (
        <>
          <Link className="back" to={back.to}>← {back.label}</Link>
          <strong>{title}</strong>
        </>
      ) : (
        <span className="brand">
          {/* The app mark, as it appears on iOS and Android — the product's own object
              rather than a piece of this palette. */}
          <img src="/zsoogi-logo.png" alt="" width={28} height={28} />
          <strong>ZsoogiNotes</strong>
        </span>
      )}

      {children}

      <div className="header-actions">
        {/* Every signed-in screen sits behind the Pro gate in App.tsx, so anyone who sees
            this header is Pro. A status, not a button — on iOS the same pill is the upgrade
            entry point and only free users see it (zsoogi-ios #69, #86). */}
        <span className="pro-badge" title="You're on ZsoogiNotes Pro">
          <span aria-hidden="true">✦</span> Pro
        </span>
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
